import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import PageShell from "../../../components/PageShell";
import DataTable from "../../../components/DataTable";
import DataForm from "../../../components/DataForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Select } from "../../../components/ui/select";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { Scanner } from "@yudiel/react-qr-scanner";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from "../../../components/ui/context-menu";
import {
  useComputers,
  useCreateComputer,
  useUpdateComputer,
  useDeleteComputer,
  useComputerStatuses,
} from "../hooks/useComputers";
import { useZones } from "../hooks/useLocations";
import { useLocationOptions } from "../hooks/useOptions";
import { selectAccessToken, selectLocation } from "../../../store/auth";
import * as playtimeApi from "../../../api/playtime";
import toast from "react-hot-toast";
import { useTranslation } from "../../../i18n";
import { useLocation, useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";

/* ───────── Computers ───────── */

const COMPUTER_COLUMNS = [
  { header: "col.location", accessor: "_locationName", type: "text" },
  { header: "col.zoneName", accessor: "_zoneName", type: "text" },
  { header: "col.computerNumber", accessor: "number", type: "number" },
  { header: "col.status", accessor: "_statusName", type: "text" },
];

const COMPUTER_CREATE_DEFAULTS = {
  locationId: "",
  zoneId: "",
  number: 1,
  apiKey: "",
  expiresAt: null,
};

const API_KEY_URL_PARAM = "apiKey";
const INSTALL_EXPIRY_URL_PARAM = "expiresAt";

// Backend-localized detail messages returned with a 422 when an installation
// QR is past its 15-minute window. Used to surface a specific error instead of
// the generic save-failed toast.
const INSTALL_EXPIRED_MESSAGES = new Set([
  "Installation QR expired, request a new one",
  "QR-код установки истёк, запросите новый",
]);

// Carries the in-progress add-computer form across a round-trip to the
// Locations page (where zones are created). Stored for one navigation and
// cleared once restored, so it never leaks into later sessions.
const PENDING_COMPUTER_FORM_KEY = "pendingComputerForm";

// Mirrors the key used in PrivateRoute.jsx. An install token opened while
// unauthenticated is stashed here because the URL fragment (and the key) can't
// survive the login redirect. Restored below, cleared once consumed.
const PENDING_INSTALL_TOKEN_KEY = "cyberpass.pendingInstallToken";

function ComputersSection({
  computers,
  zones,
  locationOptions,
  statusOptions,
  createMutation,
  updateMutation,
  deleteMutation,
  accessToken,
  currentLoginLocation,
  t,
}) {
  const [editingRow, setEditingRow] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [values, setValues] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("location");
  const [showQrScanner, setShowQrScanner] = useState(false);
  // True when the scanned/opened installation QR is older than the
  // INSTALL_WINDOW_MINUTES window. Blocks PC creation until a fresh QR is used.
  const [installExpired, setInstallExpired] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // The api key travels in the URL fragment only (#apiKey=...&expiresAt=...) —
  // fragments never reach the server and are stripped from the Referer header.
  const urlInstallToken = (() => {
    const params = new URLSearchParams(location.hash.slice(1));
    const apiKey = params.get(API_KEY_URL_PARAM);
    if (apiKey) {
      const rawExpiry = params.get(INSTALL_EXPIRY_URL_PARAM);
      const expiresAt = rawExpiry ? parseInt(rawExpiry, 10) : null;
      return {
        apiKey,
        expiresAt: Number.isFinite(expiresAt) ? expiresAt : null,
      };
    }
    // No fragment — the link may have gone through the unauthenticated login
    // round-trip, where the key can't travel in the URL. PrivateRoute stashed
    // it; it's cleared once consumed by the auto-open effect below.
    return readStashedInstallToken();
  })();
  const urlApiKey = urlInstallToken.apiKey;
  const urlExpiresAt = urlInstallToken.expiresAt;
  // Dedupes repeated onScan frames for the same QR value
  const lastScannedRef = useRef(null);

  // Build lookup maps for display names
  const locationMap = Object.fromEntries(
    (locationOptions || []).map((l) => [String(l.value), l.name]),
  );

  const zoneMap = Object.fromEntries(
    (zones || []).map((z) => [String(z.id), z.name]),
  );

  const statusMap = Object.fromEntries(
    (statusOptions || []).map((s) => [String(s.value), s.name]),
  );

  // Build zone -> location mapping (display enrichment)
  const zoneToLocation = Object.fromEntries(
    (zones || []).map((z) => [String(z.id), String(z.locationId)]),
  );

  // Guards against out-of-order async default fetches in the create form:
  // only the most recent handleFormChange call is allowed to apply its result.
  const formChangeSeq = useRef(0);

  // In the create form: on location/zone change, ask the backend for the
  // default zone and next available number — keeps the next zone & pc number
  // invariants purely back-end driven (see GET /computers/defaults)
  const handleFormChange = async (newValues) => {
    const seq = ++formChangeSeq.current;
    if (isNew && newValues.locationId !== values?.locationId) {
      try {
        const defaults = await playtimeApi.fetchComputerDefaults(accessToken, {
          locationId: newValues.locationId,
        });
        if (seq !== formChangeSeq.current) return;
        if (defaults.redirect) {
          toast.error(t("toast.noZonesForComputer"));
          setValues({ ...newValues, zoneId: "", number: "" });
          return;
        }
        setValues({
          ...newValues,
          locationId: String(defaults.locationId),
          zoneId: String(defaults.zoneId),
          number: defaults.number,
        });
      } catch {
        if (seq !== formChangeSeq.current) return;
        setValues(newValues);
      }
      return;
    }
    if (isNew && newValues.zoneId && newValues.zoneId !== values?.zoneId) {
      try {
        const defaults = await playtimeApi.fetchComputerDefaults(accessToken, {
          zoneId: newValues.zoneId,
        });
        if (seq !== formChangeSeq.current) return;
        setValues({ ...newValues, number: defaults.number });
      } catch {
        if (seq !== formChangeSeq.current) return;
        setValues(newValues);
      }
      return;
    }
    setValues(newValues);
  };

  // Persist the current add-computer form and navigate to the Locations page
  // to create a fresh zone for the selected location. The user returns via the
  // Locations page, at which point the pending form is restored automatically.
  const goCreateZone = () => {
    if (!values?.locationId) {
      toast.error(t("toast.selectLocationFirst"));
      return;
    }
    try {
      sessionStorage.setItem(PENDING_COMPUTER_FORM_KEY, JSON.stringify(values));
    } catch {
      // Non-fatal: fall through to the locations page anyway.
    }
    navigate(
      `/dashboard/super/locations?editZone=${encodeURIComponent(
        values.locationId,
      )}&returnTo=${encodeURIComponent("/dashboard/super/computers")}`,
    );
  };

  // Enrich computers with display names
  const enrichedComputers = (computers || []).map((c) => ({
    ...c,
    _locationName: locationMap[zoneToLocation[String(c.zoneId)] || ""] || "-",
    _zoneName: zoneMap[String(c.zoneId)] ?? "-",
    _statusName: statusMap[String(c.status)] ?? c.status,
  }));

  // Filter computers by search query
  const filteredComputers = (() => {
    if (!searchQuery.trim()) return enrichedComputers;
    const query = searchQuery.toLowerCase();
    return enrichedComputers.filter((c) => {
      switch (searchType) {
        case "zone":
          return String(c._zoneName).includes(query);
        case "pc":
          return String(c.number).includes(query);
        default:
          return c._locationName.toLowerCase().includes(query);
      }
    });
  })();

  // Filter zones by selected location (for add modal)
  const filteredZoneOptions = (() => {
    const selectedLocationId = values?.locationId;
    if (!selectedLocationId) return [];
    return (zones || [])
      .filter((z) => String(z.locationId) === String(selectedLocationId))
      .map((z) => ({
        value: String(z.id),
        name: String(z.name),
      }));
  })();

  // For edit mode, get the zone's location
  const editZoneLocation = (() => {
    if (!editingRow || isNew) return null;
    return zoneToLocation[String(editingRow.zoneId)] || null;
  })();

  const openEdit = (row) => {
    setEditingRow(row);
    setValues({
      id: row.id,
      zoneId: String(row.zoneId),
      number: row.number,
      apiKey: row.apiKey,
      status: row.status,
    });
    setIsNew(false);
  };

  const openCreate = useCallback(async () => {
    setInstallExpired(isInstallExpired(urlExpiresAt));
    // Try to fetch defaults from the backend
    try {
      const defaults = await playtimeApi.fetchComputerDefaults(accessToken);
      if (defaults.redirect) {
        // No zones in the current location — notify, but still open the
        // dialog so the user can pick another location manually.
        toast.error(t("toast.noZonesForComputer"));
        setEditingRow({});
        setValues({
          ...COMPUTER_CREATE_DEFAULTS,
          locationId: currentLoginLocation
            ? String(currentLoginLocation.id)
            : "",
          apiKey: urlApiKey ?? "",
          expiresAt: urlExpiresAt,
        });
        setIsNew(true);
        setShowQrScanner(false);
        return;
      }
      setEditingRow({});
      setValues({
        ...COMPUTER_CREATE_DEFAULTS,
        locationId: String(defaults.locationId),
        zoneId: String(defaults.zoneId),
        number: defaults.number,
        apiKey: urlApiKey ?? "",
        expiresAt: urlExpiresAt,
      });
      setIsNew(true);
      setShowQrScanner(false);
    } catch {
      // Fallback to static defaults if the request fails
      setEditingRow({});
      setValues({
        ...COMPUTER_CREATE_DEFAULTS,
        locationId: currentLoginLocation ? String(currentLoginLocation.id) : "",
        apiKey: urlApiKey ?? "",
        expiresAt: urlExpiresAt,
      });
      setIsNew(true);
      setShowQrScanner(false);
    }
  }, [accessToken, t, urlApiKey, urlExpiresAt, currentLoginLocation]);

  // Auto-open the create dialog when the page was opened via a QR link.
  // The ref guards against React StrictMode double-invoking the effect.
  const autoOpenedFor = useRef(null);
  useEffect(() => {
    if (!urlApiKey || autoOpenedFor.current === urlApiKey) return;
    autoOpenedFor.current = urlApiKey;
    // Consumed the stashed token (restore path) — drop it so an abandoned
    // login round-trip can't resurface a stale key on a later visit.
    try {
      sessionStorage.removeItem(PENDING_INSTALL_TOKEN_KEY);
    } catch {
      // ignore — storage may be unavailable
    }
    openCreate();
  }, [urlApiKey, openCreate]);

  // A fresh URL token supersedes any stashed one — clear the leftovers up
  // front so an old install key never wins over a new #apiKey link.
  useEffect(() => {
    if (urlApiKey) {
      try {
        sessionStorage.removeItem(PENDING_INSTALL_TOKEN_KEY);
      } catch {
        // ignore — storage may be unavailable
      }
    }
  }, [urlApiKey]);

  // Restore an add-computer form preserved across a round-trip to the
  // Locations page (zone creation). Cleared immediately so it doesn't re-open
  // on later visits.
  useEffect(() => {
    let pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem(PENDING_COMPUTER_FORM_KEY));
    } catch {
      pending = null;
    }
    if (!pending) return;
    try {
      sessionStorage.removeItem(PENDING_COMPUTER_FORM_KEY);
    } catch {
      // ignore — storage may be unavailable
    }
    if (!pending.locationId) return;
    setEditingRow({});
    setIsNew(true);
    setShowQrScanner(false);
    // Re-run the backend defaults for the pending location so the freshly
    // created zone (and next number) get selected automatically.
    const seq = ++formChangeSeq.current;
    playtimeApi
      .fetchComputerDefaults(accessToken, {
        locationId: String(pending.locationId),
      })
      .then((defaults) => {
        if (seq !== formChangeSeq.current) return;
        if (defaults.redirect) {
          toast.error(t("toast.noZonesForComputer"));
          setValues({
            ...COMPUTER_CREATE_DEFAULTS,
            locationId: String(pending.locationId),
            apiKey: pending.apiKey ?? "",
          });
          return;
        }
        setValues({
          ...COMPUTER_CREATE_DEFAULTS,
          locationId: String(defaults.locationId),
          zoneId: String(defaults.zoneId),
          number: defaults.number,
          apiKey: pending.apiKey ?? "",
        });
      })
      .catch(() => {
        if (seq !== formChangeSeq.current) return;
        setValues({
          ...COMPUTER_CREATE_DEFAULTS,
          locationId: String(pending.locationId),
          apiKey: pending.apiKey ?? "",
        });
      });
  }, []);

  // Drop the api key from the URL fragment once the computer has been saved
  // (or the dialog closed).
  const clearApiKeyParam = () => {
    const hash = window.location.hash.replace(/^#/, "");
    const hashParams = new URLSearchParams(hash);
    if (!hashParams.has(API_KEY_URL_PARAM)) return;
    hashParams.delete(API_KEY_URL_PARAM);
    const nextHash = hashParams.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}${
        nextHash ? `#${nextHash}` : ""
      }`,
    );
  };

  // Clear the api key from the form and drop the URL fragment
  const clearApiKey = () => {
    setValues((v) => ({ ...v, apiKey: "" }));
    clearApiKeyParam();
  };

  const handleSave = () => {
    if (!values) return;
    if (isNew && installExpired) {
      toast.error(t("toast.installExpired"));
      return;
    }
    const req = {
      zoneId: Number(values.zoneId),
      number: Number(values.number),
      apiKey: values.apiKey,
    };
    if (isNew && values.expiresAt != null) req.expiresAt = values.expiresAt;
    if (!isNew) req.status = values.status;
    const mutation = isNew ? createMutation : updateMutation;
    const payload = isNew ? req : { id: editingRow.id, ...req };
    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success(
          isNew ? t("toast.computerCreated") : t("toast.computerUpdated"),
        );
        clearApiKeyParam();
        setEditingRow(null);
        setValues(null);
      },
      onError: (err) => {
        if (isInstallExpiredError(err)) {
          setInstallExpired(true);
          toast.error(t("toast.installExpired"));
          return;
        }
        toast.error(t("toast.computerSaveFailed"));
      },
    });
  };

  const handleArchive = () => {
    if (!archiveTarget) return;
    deleteMutation.mutate(archiveTarget.id, {
      onSuccess: () => {
        toast.success(t("toast.computerArchived"));
        setArchiveTarget(null);
      },
      onError: () => toast.error(t("toast.computerArchiveFailed")),
    });
  };

  const renderArchiveMenu = (row, children) => (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem
          className="bg-c-red-500 hover:bg-c-red-600 text-white"
          onClick={() => setArchiveTarget(row)}
        >
          {t("common.archive")}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );

  // Add modal form fields: Location dropdown, then Zone dropdown filtered by location
  const computerAddFormFields = [
    {
      name: "locationId",
      label: "form.locationId",
      type: "select",
      fetchOptions: () => Promise.resolve(locationOptions || []),
    },
    {
      name: "zoneId",
      label: "form.zoneId",
      type: "select",
      fetchOptions: () => Promise.resolve(filteredZoneOptions),
      disabled: !values?.locationId,
      renderBelow: (formValues) => {
        const locId = formValues?.locationId;
        let label = t("computer.needNewZone");
        if (!locId) label = t("computer.selectLocationHint");
        return (
          <button
            type="button"
            onClick={goCreateZone}
            className="mt-1 text-sm text-accent underline hover:text-c-gray-300 cursor-pointer"
          >
            {label}
          </button>
        );
      },
    },
    {
      name: "number",
      label: "form.computerNumber",
      type: "number",
    },
    {
      name: "apiKey",
      label: "form.apiKey",
      type: "text",
      suffix: <Trash2 size={22} />,
      onSuffixClick: clearApiKey,
    },
  ];

  // Edit modal form fields: readonly Location and Zone text, editable number and status
  const computerEditFormFields = [
    {
      name: "id",
      label: "col.id",
      type: "number",
      disabled: true,
    },
    {
      name: "_locationDisplay",
      label: "form.locationId",
      type: "text",
      disabled: true,
    },
    {
      name: "_zoneDisplay",
      label: "form.zoneId",
      type: "text",
      disabled: true,
    },
    {
      name: "number",
      label: "form.computerNumber",
      type: "number",
    },
    {
      name: "status",
      label: "form.status",
      type: "select",
      fetchOptions: () => Promise.resolve(statusOptions || []),
      required: false,
    },
  ];

  // Prepare edit values with display fields
  let editValues = values;
  if (values && !isNew) {
    editValues = {
      ...values,
      _locationDisplay: locationMap[editZoneLocation] || "-",
      _zoneDisplay: zoneMap[String(editingRow?.zoneId)] ?? "-",
    };
  }

  return (
    <>
      <div className="flex justify-between items-center h-14">
        <div className="flex gap-2 items-center px-1">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("common.search")}
            className="lg:w-96"
          />
          <Select
            value={searchType}
            onChange={(e) => {
              setSearchType(e.target.value);
              setSearchQuery("");
            }}
            className="w-auto"
          >
            <option value="location">{t("col.location")}</option>
            <option value="zone">{t("col.zoneName")}</option>
            <option value="pc">{t("col.computerNumber")}</option>
          </Select>
        </div>
        <Button onClick={openCreate}>{t("common.add")}</Button>
      </div>
      <DataTable
        columns={COMPUTER_COLUMNS}
        data={filteredComputers}
        onRowClick={openEdit}
        renderContextMenu={renderArchiveMenu}
      />

      <Dialog
        open={!!editingRow}
        onOpenChange={(o) => {
          if (!o) {
            setEditingRow(null);
            setValues(null);
            setShowQrScanner(false);
            clearApiKeyParam();
          }
        }}
      >
        <DialogContent className="left-auto right-0 top-0 translate-x-0 translate-y-0 h-dvh max-h-none rounded-none sm:rounded-none border-r-0 border-t-0 border-b-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right min-w-0 sm:min-w-[480px] grid-rows-[auto_1fr_auto]">
          <DialogHeader>
            <DialogTitle>
              {isNew ? t("dialog.addComputer") : t("dialog.editRecord")}
            </DialogTitle>
          </DialogHeader>
          <form
            id="computer-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="flex-1 overflow-y-auto px-2"
          >
            <DataForm
              fields={isNew ? computerAddFormFields : computerEditFormFields}
              values={isNew ? values : editValues}
              onChange={handleFormChange}
            />
            {isNew && installExpired && (
              <div className="mt-4 p-3 rounded-lg border border-red-400/40 bg-red-500/10 text-sm text-red-200">
                {t("computer.installExpiredWarning")}
              </div>
            )}
            {isNew && (
              <div className="mt-4">
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  onClick={() => {
                    // Fresh session for the dedupe guard on each open
                    if (!showQrScanner) lastScannedRef.current = null;
                    setShowQrScanner((s) => !s);
                  }}
                >
                  {showQrScanner
                    ? t("computer.closeScanner")
                    : t("computer.scanKey")}
                </Button>
                {showQrScanner && (
                  <div className="mt-3">
                    <Scanner
                      onScan={(result) => {
                        const scanned = result[0]?.rawValue;
                        if (!scanned || lastScannedRef.current === scanned)
                          return;
                        lastScannedRef.current = scanned;
                        const { apiKey, expiresAt } =
                          extractInstallToken(scanned);
                        if (apiKey) {
                          const expired = isInstallExpired(expiresAt);
                          setInstallExpired(expired);
                          setValues((v) => ({ ...v, apiKey, expiresAt }));
                          if (expired) {
                            toast.error(t("toast.installExpired"));
                          } else {
                            toast.success(t("toast.apiKeyPrefilled"));
                          }
                          setShowQrScanner(false);
                        } else {
                          toast.error(t("toast.apiKeyNotFound"));
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </form>
          <DialogFooter className="flex gap-2">
            <Button
              type="submit"
              form="computer-form"
              disabled={isNew && installExpired}
            >
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!archiveTarget}
        onOpenChange={(o) => {
          if (!o) setArchiveTarget(null);
        }}
        itemName={`${t("playtime.computer").toLowerCase()} #${archiveTarget?.number || ""}`}
        onConfirm={handleArchive}
      />
    </>
  );
}

/* ───────── Main Page ───────── */

export default function ComputersPage() {
  const { t } = useTranslation();
  const accessToken = useSelector(selectAccessToken);
  const currentLoginLocation = useSelector(selectLocation);

  const { data: zones } = useZones();
  const { data: computers, isLoading: computersLoading } = useComputers();
  const { data: locationOptions } = useLocationOptions();
  const { data: statusOptions } = useComputerStatuses();

  const createComputerMutation = useCreateComputer();
  const updateComputerMutation = useUpdateComputer();
  const deleteComputerMutation = useDeleteComputer();

  return (
    <PageShell title={t("page.playtime")}>
      {computersLoading ? (
        <p className="text-c-gray-400">{t("common.loading")}</p>
      ) : (
        <ComputersSection
          computers={computers}
          zones={zones}
          locationOptions={locationOptions}
          statusOptions={statusOptions}
          createMutation={createComputerMutation}
          updateMutation={updateComputerMutation}
          deleteMutation={deleteComputerMutation}
          accessToken={accessToken}
          currentLoginLocation={currentLoginLocation}
          t={t}
        />
      )}
    </PageShell>
  );
}

/* ───────── Helpers ───────── */

/**
 * Extract the installation API key and its optional expiry timestamp from a
 * scanned/opened URL string. Supports only URL fragments (#apiKey=...) — the
 * key must never travel as a query param because query strings leak into
 * history, logs, and Referer.
 * @param {string} value
 * @returns {{ apiKey: string | null, expiresAt: number | null }}
 */
const extractInstallToken = (value) => {
  if (!value) return { apiKey: null, expiresAt: null };
  try {
    return _getInstallTokenParams(new URL(value));
  } catch {
    // Some scanners strip the scheme — retry with https://
    try {
      return _getInstallTokenParams(new URL(`https://${value}`));
    } catch {
      return { apiKey: null, expiresAt: null };
    }
  }
};

const _getInstallTokenParams = (url) => {
  const hash = url.hash.replace(/^#/, "");
  const params = new URLSearchParams(hash);
  const apiKey = params.get(API_KEY_URL_PARAM);
  const rawExpiry = params.get(INSTALL_EXPIRY_URL_PARAM);
  const expiresAt = rawExpiry ? parseInt(rawExpiry, 10) : null;
  return {
    apiKey,
    expiresAt: Number.isFinite(expiresAt) ? expiresAt : null,
  };
};

/**
 * True when a Unix timestamp in seconds is in the past (i.e. the 15-minute
 * installation window has already passed).
 * @param {number|null} expiresAt
 * @returns {boolean}
 */
const isInstallExpired = (expiresAt) =>
  expiresAt != null && Math.floor(Date.now() / 1000) > expiresAt;

/**
 * True when a rejected API call carries the "installation QR expired" detail.
 * The backend responds to an expired key with 422 and a localized detail
 * message; the api layer throws the raw response body, so we parse it here.
 * @param {unknown} err
 * @returns {boolean}
 */
const isInstallExpiredError = (err) => {
  const detail = _getApiErrorDetail(err);
  return detail != null && INSTALL_EXPIRED_MESSAGES.has(detail);
};

/**
 * Pull the FastAPI `detail` string out of a thrown response body.
 * @param {unknown} err
 * @returns {string | null}
 */
const _getApiErrorDetail = (err) => {
  const raw = typeof err === "string" ? err : err?.message;
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed?.detail === "string" ? parsed.detail : null;
  } catch {
    return null;
  }
};

/**
 * Pick up the install token stashed by `stashInstallToken` in PrivateRoute.jsx
 * when a QR link was opened while unauthenticated. Cleared by the auto-open
 * effect once consumed.
 * @returns {{ apiKey: string | null, expiresAt: number | null }}
 */
const readStashedInstallToken = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_INSTALL_TOKEN_KEY);
    if (!raw) return { apiKey: null, expiresAt: null };
    const parsed = JSON.parse(raw);
    return {
      apiKey: typeof parsed?.apiKey === "string" ? parsed.apiKey : null,
      expiresAt: Number.isFinite(parsed?.expiresAt) ? parsed.expiresAt : null,
    };
  } catch {
    return { apiKey: null, expiresAt: null };
  }
};
